// src/components/Chatbot.js
import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, onSnapshot, query, orderBy, serverTimestamp } from 'firebase/firestore';
import {db} from "../firebase/firebase";
import {useDarkMode} from "../contexts/darkModeContext";
import {Button, Input} from "@headlessui/react";

// Define your chatbot's persona here
const BOT_PERSONA = {
    name: "TED",
    instruction: "You are ted, a helpful and friendly assistant for the company 'Plant Food Company Inc.'. Your tone should be professional yet approachable. Use the provided context to answer user questions."
};

function Chatbot() {
    const { darkMode } = useDarkMode();
  const [messages, setMessages] = useState([
    // 1. ADD THE START PROMPT / WELCOME MESSAGE
    {
      id: 'initial-bot-message',
      prompt: null, // No user prompt for the first message
      response: `Hello! I am ${BOT_PERSONA.name}. Your Virtual Plant Food Assistant! I may be young but I am able to answer a lot of questions that you may have on our company. I have been trained on the Employee Handbook, the Sales Tool, your past account activity, and much more! How can I help you get started today?`,
      status: { state: 'COMPLETED' }, // Manually set status for welcome message
      createTime: new Date()
    }
  ]);

  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const CHAT_COLLECTION = 'ted';

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  useEffect(() => {
    const q = query(collection(db, CHAT_COLLECTION), orderBy('createTime', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const updatedMessages = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      // A simple way to merge initial state with Firestore data
      // This assumes you clear your 'chats' collection periodically for a clean start
      const combinedMessages = [
          ...messages.filter(m => m.id === 'initial-bot-message'),
          ...updatedMessages
      ];

      setMessages(combinedMessages);
      if (updatedMessages.length > 0 && updatedMessages[updatedMessages.length - 1].status) {
         setLoading(false);
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line
  }, []); // Note: The dependency array is empty on purpose for this strategy

  const sendMessage = async (e) => {
    e.preventDefault();
    if (input.trim() === '') return;
    const userMessage = input;
    setInput('');
    setLoading(true);

    try {
      // 2. ADD THE SYSTEM INSTRUCTION TO THE DOCUMENT
      await addDoc(collection(db, CHAT_COLLECTION), {
        prompt: userMessage,
        // The extension will see this and use it to guide Gemini
        systemInstruction: BOT_PERSONA.instruction,
        createTime: serverTimestamp()
      });
    } catch (error) {
      console.error("Error sending message: ", error);
      setLoading(false);
    }
  };

  return (
    <div className={`${darkMode ? 'bg-darkMainColor border-neutral-700' : 'bg-white'} font-carlita w-full mx-auto my-8 border-2 rounded-lg shadow-lg flex flex-col h-full`}>
      {/* Message Display Area */}
      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        {messages.map((msg) => (
          <div key={msg.id} className="flex flex-col">
            
            {/* User's Prompt */}
            <div className="border border-blue-600 p-1.5 rounded-lg rounded-br-none self-end max-w-[80%]">
              <p>{msg.prompt}</p>
            </div>

            {/* Gemini's Response - conditional rendering */}
            {msg.status && (
              <div className="mt-2">
                {msg.status.state === 'COMPLETED' && (
                  <div className={`border w-fit border-green-600 p-3 rounded-xl rounded-bl-none self-start max-w-[80%] whitespace-pre-wrap`}>
                    <p>{msg.response}</p>
                  </div>
                )}
                {msg.status.state === 'PROCESSING' && (
                  <div className="bg-gray-200 text-gray-500 p-3 rounded-2xl rounded-bl-none self-start">
                    <p>Gemini is thinking...</p>
                  </div>
                )}
                {msg.status.state === 'ERRORED' && (
                  <div className="bg-red-100 text-red-600 p-3 rounded-lg self-start">
                    <p>Error: {msg.status.error}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
         {/* Show loading indicator while waiting for the first response */}
        {loading && messages.length > 0 && !messages[messages.length - 1].status && (
            <div className="bg-gray-200 text-gray-500 p-3 rounded-2xl rounded-bl-none self-start">
              <p>Gemini is thinking...</p>
            </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Form */}
      <div className={`p-2 border-t-2 ${darkMode ? 'border-neutral-700' : ''}`}>
        <form onSubmit={sendMessage} className="flex items-center space-x-2">
          <Input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask Ted Anything..."
            disabled={loading}
            className="flex-1 px-2 py-1 border text-sm border-blue-600 bg-blue-600/25 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          />
          <Button
            type="submit"
            disabled={loading}
            className="bg-green-600 text-white font-semibold py-1 px-4 text-sm rounded-lg hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300 disabled:cursor-not-allowed transition-colors"
          >
            Chat
          </Button>
        </form>
      </div>
    </div>
  );
}

export default Chatbot;
